import { type FC, useEffect } from "react";
import BrandDropdown from "@/components/common/BrandDropdown";
import { useGetShareOfVoiceMetricsTableData } from "@/components/share-of-voice/metrics-table/useMetricsTable";
import ColorSelector from "@/components/ui/ColorSelector.tsx";
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
} from "@/components/ui/dialog.tsx";
import { Label } from "@/components/ui/field.tsx";
import Autocomplete from "@/components/ui/molecules/autocomplete/Autocomplete.tsx";
import type {
  AutocompleteGroup,
  AutocompleteOption,
} from "@/components/ui/molecules/autocomplete/Autocomplete.type";
import { SavedViewsDropdown } from "@/components/ui/SavedViewsDropdown";
import { JollyTextField } from "@/components/ui/textfield.tsx";
import { useFeature } from "@/lib/feature-management";
import { useFeedOptions } from "@/store/news-feeds/news-feeds.selectors";
import { useKeyMessageOptions } from "@/store/share-of-voice/segment/segment.selectors.ts";
import useSegmentStore from "@/store/share-of-voice/segment/segment.slice.ts";
import {
  createSegment,
  getKeyMessageGroupsForSegment,
  updateSegment,
} from "@/store/share-of-voice/segment/segment-async.actions.ts";

interface CreateSegmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  updateSegmentId: string | null;
}

const CreateOrEditSegmentModal: FC<CreateSegmentModalProps> = ({
  isOpen,
  onClose,
  updateSegmentId,
}) => {
  const enableSOVExp = useFeature("enable-sov-exp", false);

  const feedOptions = useFeedOptions();
  const feedId = useSegmentStore((state) => state.feedId);
  const selectedFeed = feedOptions.find((feed) => feed.feedId === feedId);
  const isCreatingSegment = useSegmentStore((state) => state.isCreatingSegment);
  const isUpdatingSegment = useSegmentStore((state) => state.isUpdatingSegment);
  const keyMessageId = useSegmentStore((state) => state.keyMessageId);
  const savedViewId = useSegmentStore((state) => state.savedViewId);
  const resetSegment = useSegmentStore((state) => state.resetSegment);
  const segmentColor = useSegmentStore((state) => state.color);
  const segmentName = useSegmentStore((state) => state.name);
  const setFeedId = useSegmentStore((state) => state.setFeedId);
  const setKeyMessageId = useSegmentStore((state) => state.setKeyMessageId);
  const setSavedViewId = useSegmentStore((state) => state.setSavedViewId);
  const setSegmentColor = useSegmentStore((state) => state.setSegmentColor);
  const setSegmentName = useSegmentStore((state) => state.setSegmentName);

  // Access key message options from selector
  const keyMessageOptions =
    useKeyMessageOptions() as AutocompleteGroup<AutocompleteOption>[];
  const isFetchingKeyMessagesAndGroups = useSegmentStore(
    (state) => state.isFetchingKeyMessagesAndGroups,
  );
  const { refetch: refetchTableData } = useGetShareOfVoiceMetricsTableData();

  useEffect(() => {
    if (feedId) {
      getKeyMessageGroupsForSegment({ feedId });
    }
  }, [feedId]);

  // Handles the segment creation process
  const handleCreateSegment = async () => {
    try {
      await createSegment();
      // Refetch the metrics table data after successful creation
      refetchTableData();
    } catch (error) {
      // Error handling is done in the createSegment action
      console.error("Failed to create segment:", error);
    }
  };

  // Handles the segment update process
  const handleUpdateSegment = async () => {
    if (!updateSegmentId) return;

    await updateSegment({
      id: updateSegmentId,
      name: segmentName,
      hexColor: segmentColor,
    });
  };

  // Handle onClose
  const handleClose = () => {
    resetSegment();
    onClose();
  };

  // Check if form is valid for submission
  const isFormValid = updateSegmentId
    ? Boolean(segmentName.trim()) // When editing, only require segment name
    : Boolean(feedId && segmentName.trim() && segmentColor); // When creating, require feedId, name, and color

  return (
    <DialogOverlay
      isOpen={isOpen}
      aria-labelledby={"Create or edit a SOV segment"}
    >
      <DialogContent size="default" onClose={handleClose}>
        <DialogHeader
          title={`${updateSegmentId ? "Edit" : "Create"} a SOV segment`}
        />
        <DialogBody>
          {!updateSegmentId && (
            <p className={"text-gray-600 text-xs mb-5"}>
              Select a brand/topic feed to get started. Next select an
              associated {enableSOVExp ? "saved view" : "key topic"} and name
              your segment.
            </p>
          )}
          {/** If updateSegmentId is not null, we are editing a segment */}
          {!updateSegmentId && (
            <>
              <div className={"mb-5"}>
                <Label variant={"secondary"} size={"sm"}>
                  Brand/Topic*
                </Label>
                <BrandDropdown feedId={feedId} setFeedId={setFeedId} />
              </div>
              <div className={"mb-5"}>
                {enableSOVExp ? (
                  <SavedViewsDropdown
                    feedId={feedId || undefined}
                    value={savedViewId}
                    onValueChange={(value, label) => {
                      setSavedViewId(value ?? null);
                      const brandName = selectedFeed?.name || "";
                      setSegmentName(
                        brandName && label
                          ? `${brandName}: ${label}`
                          : label || "",
                      );
                    }}
                    placeholder={"Select a saved view"}
                    disabled={!feedId}
                    isRequired={true}
                  />
                ) : (
                  <Autocomplete
                    label="Key Topic"
                    labelSize={"sm"}
                    options={keyMessageOptions}
                    onValueChange={(value, label) => {
                      setKeyMessageId(value ?? null);
                      const brandName = selectedFeed?.name || "";
                      setSegmentName(
                        brandName && label
                          ? `${brandName}: ${label}`
                          : label || "",
                      );
                    }}
                    value={keyMessageId}
                    placeholder={"Add a key topic"}
                    disabled={!feedId || isFetchingKeyMessagesAndGroups}
                    emptyMessage="No key topics found for this brand"
                    labelVariant="secondary"
                    isPending={isFetchingKeyMessagesAndGroups}
                    focusText={"Type to search or select a key topic"}
                  />
                )}
              </div>
            </>
          )}
          <JollyTextField
            size={"sm"}
            label={
              updateSegmentId
                ? "Segment name"
                : "What do you want to name this segment?"
            }
            labelVariant={"secondary"}
            placeholder={'ex "Brand name + Environmental Impact"'}
            value={segmentName}
            onChange={(value) => setSegmentName(value)}
            isDisabled={isCreatingSegment}
            isRequired={true}
          />
          <ColorSelector
            className={"pl-0.5 pt-5"}
            onSelect={(color) => setSegmentColor(color)}
            selectedColor={segmentColor}
          />
        </DialogBody>
        <DialogFooter
          primaryLabel={updateSegmentId ? "Save" : "Create segment"}
          secondaryLabel={"Cancel"}
          secondaryAction={handleClose}
          primaryAction={
            updateSegmentId ? handleUpdateSegment : handleCreateSegment
          }
          primaryDisabled={
            !isFormValid || isCreatingSegment || isUpdatingSegment
          }
          isPending={isCreatingSegment || isUpdatingSegment}
        />
      </DialogContent>
    </DialogOverlay>
  );
};
export default CreateOrEditSegmentModal;
